/**
 * AudioRoomMiniPlayer — Bottom-right expandable panel.
 *
 * ★ FULL FEATURE PARITY with AudioRoom.js ★
 *
 * Collapsed: pill in bottom-right with LIVE dot + title + count + speaker.
 * Expanded: scrollable panel with EVERY control from the main audio room:
 *   - Speaker grid (speaking ring, mic badge, host/co-host badge, reactions, hand-raised)
 *   - Speaker invite banner
 *   - Listener grid (reactions, hand-raised, promote button for hosts)
 *   - Participant context menus (demote, make co-host, revoke co-host, kick, invite to speak)
 *   - Emoji reaction bar (👍🔥👏❤️😂✋)
 *   - Host extras (Mute All, Record, Lock)
 *   - Speaker requests panel (accept/reject + accept all)
 *   - Controls: Request Speaker | Mic | Step Down | End Room | Leave
 */
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAudioRoom } from '../../Context/AudioRoomContext';
import API from '../../Network/API';
import './AudioRoomMiniPlayer.css';
import './AudioRoomMiniPlayerSuperUI.css';
const AudioRoomMiniPlayer = () => {
    const location = useLocation();
    const {
        activeRoom,
        currentRole,
        isConnected,
        isMuted,
        isSpeaker,
        canManage,
        activeSpeakers,
        unmutedParticipants,
        handRaised,
        reactions,
        isRecording,
        isRoomLocked,
        isMuteLocked,
        muteLockMessage,
        lastActiveSpeakerName,
        toggleMute,
        muteAll,
        toggleLock,
        startRecording,
        stopRecording,
        requestToSpeak,
        lowerHand,
        sendReaction,
        stepDown,
        inviteToSpeak,
        revokeCoHost,
        handleLeaveRoom,
        handleEndRoom,
        refreshRoomDetails,
        currentUser,
        speakerInvite,
        acceptSpeakInvite,
        rejectSpeakInvite,
        navigate,
        speakerRequestNotification,
        dismissSpeakerRequest,
    } = useAudioRoom();

    const [isExpanded, setIsExpanded] = useState(false);
    const [managingParticipant, setManagingParticipant] = useState(null);
    const [showEndConfirm, setShowEndConfirm] = useState(false);

    if (!activeRoom || !isConnected) return null;
    if (location.pathname === '/audio-rooms') return null;
    // Live video streams have their own page — hide the mini player there.
    const isVideoRoom = activeRoom.room_type === 'video';
    if (isVideoRoom && location.pathname.startsWith('/live-stream')) return null;
    const fullViewPath = isVideoRoom ? '/live-stream' : '/audio-rooms';

    const participants = activeRoom.participants || [];
    const speakerRoles = ['host', 'co_host', 'speaker'];
    const roleOrder = { host: 0, co_host: 1, speaker: 2 };
    const speakers = participants
        .filter(p => speakerRoles.includes(p.role))
        .sort((a, b) => (roleOrder[a.role] ?? 3) - (roleOrder[b.role] ?? 3));
    const listeners = participants.filter(p => p.role === 'listener');
    const handRaisedListeners = listeners.filter(p => p.hand_raised_at);
    const totalParticipants = participants.length;

    // Avatar URL helper
    const getAvatarUrl = (user) => {
        let img = user?.image || user?.avatar;
        if (!img) return null;
        
        // Sanitize broken Mac native php artisan serve 8080 links to proper Nginx proxy bounds
        if (img.startsWith('http') && img.includes(':8080/')) {
            const baseUrl = process.env.REACT_APP_API_URL_HTTPS || process.env.REACT_APP_API_URL;
            if (baseUrl) img = img.replace(/^http:\/\/[^\/]+:8080\//, baseUrl.replace('/api', '') + '/');
        }

        if (img.startsWith('http')) return img;
        
        const base = process.env.REACT_APP_API_URL_HTTPS || process.env.REACT_APP_API_URL;
        return `${base?.replace('/api', '')}/storage/avatar/${img}`;
    };

    // Reaction for user
    const getReactionForUser = (userId) => {
        const userReactions = reactions.filter(r => r.userId == userId);
        return userReactions.length > 0 ? userReactions[userReactions.length - 1].emoji : null;
    };

    // Active speaker name — check: activeSpeakers > unmutedParticipants > lastActiveSpeakerName
    const activeSpeakerName = (() => {
        // 1. Real-time voice activity (LiveKit ActiveSpeakersChanged)
        for (const p of speakers) {
            const id = String(p.user_id);
            if (activeSpeakers.has(`${id}_web`) || activeSpeakers.has(`${id}_mobile`)) {
                return p.user?.name || 'Someone';
            }
        }
        // 2. Unmuted mic (TrackMuted/TrackUnmuted — reliable), skip self
        const currentUserId = currentUser?.id;
        for (const p of speakers) {
            if (String(p.user_id) === String(currentUserId)) continue;
            const id = String(p.user_id);
            if (unmutedParticipants.has(`${id}_web`) || unmutedParticipants.has(`${id}_mobile`)) {
                return p.user?.name || 'Someone';
            }
        }
        // 3. Persistent last speaker (8s timeout cache)
        if (lastActiveSpeakerName) return lastActiveSpeakerName;
        // No one is speaking — return null so UI shows LIVE dot + room title
        return null;
    })();

    return (
        <div className={`ar-side-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
            {/* ═══ SPEAKER REQUEST TOAST (collapsed, host/co-host only) ═══ */}
            {!isExpanded && speakerRequestNotification && (
                <div
                    className="ar-side-speak-toast"
                    onClick={() => { dismissSpeakerRequest(); setIsExpanded(true); }}
                    key={speakerRequestNotification.id}
                >
                    <span className="ar-side-speak-toast-emoji">✋</span>
                    <div className="ar-side-speak-toast-text">
                        <span className="ar-side-speak-toast-name">{speakerRequestNotification.name}</span>
                        <span className="ar-side-speak-toast-sub">wants to speak</span>
                    </div>
                    <button
                        className="ar-side-speak-toast-dismiss"
                        onClick={(e) => { e.stopPropagation(); dismissSpeakerRequest(); }}
                    >
                        <i className="fa fa-times" />
                    </button>
                </div>
            )}
            {/* ═══ COLLAPSED PILL (X Spaces style) ═══ */}
            {!isExpanded && (
                <div className="ar-side-collapsed" onClick={() => setIsExpanded(true)}>
                    <div className="ar-side-collapsed-left">
                        {/* Line 1: Equalizer + Speaker name (hero) */}
                        <div className="ar-side-collapsed-hero">
                            {activeSpeakerName ? (
                                <i className="fa fa-signal ar-side-eq-icon" />
                            ) : (
                                <span className="ar-side-live-dot-green" />
                            )}
                            <span className="ar-side-collapsed-speaker-name">
                                {activeSpeakerName || activeRoom.title}
                            </span>
                        </div>
                        {/* Line 2: +count others • Room title */}
                        <span className="ar-side-collapsed-sub">
                            {activeSpeakerName
                                ? `+${Math.max(totalParticipants - 1, 0)} others • ${activeRoom.title}`
                                : `+${totalParticipants} listening`
                            }
                        </span>
                    </div>
                    <button
                        className="ar-side-close-btn"
                        onClick={(e) => { e.stopPropagation(); handleLeaveRoom(); }}
                        title="Leave"
                    >
                        <i className="fa fa-times" />
                    </button>
                </div>
            )}

            {/* ═══ EXPANDED PANEL ═══ */}
            {isExpanded && (
                <div className="ar-side-expanded" onClick={() => setManagingParticipant(null)}>
                    {/* ─── Header ─── */}
                    <div className="ar-side-header">
                        <button
                            className="ar-side-collapse-btn"
                            onClick={() => setIsExpanded(false)}
                            title="Minimize"
                        >
                            <i className="fa fa-chevron-down" />
                        </button>
                        <div className="ar-side-header-info">
                            <span className="ar-side-live-badge">
                                <span className="ar-side-live-dot" />
                                LIVE
                            </span>
                            {(activeRoom.egress_id || isRecording) && (
                                <span className="ar-side-rec-badge">
                                    <span className="ar-side-rec-dot" />
                                    REC
                                </span>
                            )}
                            {isRoomLocked && (
                                <span className="ar-side-lock-icon">
                                    <i className="fa fa-lock" />
                                </span>
                            )}
                            <span className="ar-side-header-count">
                                🎙 {speakers.length} &nbsp; 🎧 {listeners.length}
                            </span>
                        </div>
                        <button
                            className="ar-side-open-btn"
                            onClick={() => navigate(fullViewPath)}
                            title="Open full view"
                        >
                            <i className="fa fa-expand" />
                        </button>
                    </div>

                    {/* ─── Title + Description ─── */}
                    <h3 className="ar-side-title">{activeRoom.title}</h3>
                    {activeRoom.description && (
                        <p className="ar-side-description">{activeRoom.description}</p>
                    )}

                    {/* ─── Speaker Invite Banner ─── */}
                    {speakerInvite && (
                        <div className="ar-side-invite-banner">
                            <div className="ar-side-invite-info">
                                <i className="fa fa-microphone" />
                                <span><strong>{speakerInvite.fromName}</strong> invited you to speak</span>
                            </div>
                            <div className="ar-side-invite-actions">
                                <button className="ar-side-invite-decline" onClick={rejectSpeakInvite}>Decline</button>
                                <button className="ar-side-invite-accept" onClick={acceptSpeakInvite}>Accept</button>
                            </div>
                        </div>
                    )}

                    {/* ─── SPEAKERS ─── */}
                    <div className="ar-side-section-label">Speakers ({speakers.length})</div>
                    <div className="ar-side-grid">
                        {speakers.map((p) => {
                            const isSpeaking = [...activeSpeakers].some(id => id.startsWith(String(p.user_id) + '_'));
                            const isMicOpen = [...(unmutedParticipants || new Set())].some(id => id.startsWith(String(p.user_id) + '_'));
                            const emojiReaction = getReactionForUser(p.user_id);
                            const avatarUrl = getAvatarUrl(p.user);
                            return (
                                <div
                                    key={p.id}
                                    className={`ar-side-grid-item ${isSpeaking ? 'speaking' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (canManage && p.user_id !== currentUser?.id) {
                                            setManagingParticipant(managingParticipant?.id === p.id ? null : p);
                                        }
                                    }}
                                    style={{ cursor: canManage && p.user_id !== currentUser?.id ? 'pointer' : 'default' }}
                                >
                                    <div className={`ar-side-grid-avatar ${isSpeaking ? 'speaking' : ''}`}>
                                        {avatarUrl ? (
                                            <img src={avatarUrl} alt="" onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                                        ) : null}
                                        <span style={avatarUrl ? { display: 'none' } : {}}>
                                            {(p.user?.name || '?')[0].toUpperCase()}
                                        </span>
                                        {p.role === 'host' && <div className="ar-side-grid-badge">👑</div>}
                                        {p.role === 'co_host' && <div className="ar-side-grid-badge">⭐</div>}
                                        <span className={`ar-side-mic-icon ${isMicOpen ? 'active' : 'muted'}`}>
                                            <i className={`fa ${isMicOpen ? 'fa-microphone' : 'fa-microphone-slash'}`} />
                                        </span>
                                        {emojiReaction && <span className="ar-side-emoji-pop" key={Date.now()}>{emojiReaction}</span>}
                                    </div>
                                    <span className="ar-side-grid-name">{p.user?.name || 'User'}</span>
                                    {p.hand_raised_at && <span className="ar-side-hand">✋</span>}

                                    {/* Context menu */}
                                    {managingParticipant?.id === p.id && (
                                        <div className="ar-side-ctx-menu" onClick={(e) => e.stopPropagation()}>
                                            <button onClick={async () => {
                                                try { await API.post(`/v1/audio-rooms/${activeRoom.id}/demote/${p.user_id}`); refreshRoomDetails(); } catch (e) { console.error(e); }
                                                setManagingParticipant(null);
                                            }}>
                                                <i className="fa fa-arrow-down" /> Move to listeners
                                            </button>
                                            {currentRole === 'host' && p.role !== 'co_host' && (
                                                <button onClick={async () => {
                                                    try { await API.post(`/v1/audio-rooms/${activeRoom.id}/co-host/${p.user_id}`); refreshRoomDetails(); } catch (e) { console.error(e); }
                                                    setManagingParticipant(null);
                                                }}>
                                                    <i className="fa fa-star" /> Make co-host
                                                </button>
                                            )}
                                            {currentRole === 'host' && p.role === 'co_host' && (
                                                <button onClick={async () => {
                                                    try { await revokeCoHost(p.user_id); } catch (e) { console.error(e); }
                                                    setManagingParticipant(null);
                                                }}>
                                                    <i className="fa fa-star-half-alt" /> Revoke co-host
                                                </button>
                                            )}
                                            <button className="danger" onClick={async () => {
                                                try { await API.post(`/v1/audio-rooms/${activeRoom.id}/kick/${p.user_id}`); refreshRoomDetails(); } catch (e) { console.error(e); }
                                                setManagingParticipant(null);
                                            }}>
                                                <i className="fa fa-ban" /> Remove
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* ─── LISTENERS ─── */}
                    {listeners.length > 0 && (
                        <>
                            <div className="ar-side-section-label">Listeners ({listeners.length})</div>
                            <div className="ar-side-grid">
                                {listeners.map((p) => {
                                    const emojiReaction = getReactionForUser(p.user_id);
                                    const avatarUrl = getAvatarUrl(p.user);
                                    return (
                                        <div
                                            key={p.id}
                                            className={`ar-side-grid-item small ${p.hand_raised_at ? 'hand-raised' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (canManage && p.user_id !== currentUser?.id) {
                                                    setManagingParticipant(managingParticipant?.id === p.id ? null : { ...p, _type: 'listener' });
                                                }
                                            }}
                                            style={{ cursor: canManage && p.user_id !== currentUser?.id ? 'pointer' : 'default' }}
                                        >
                                            <div className="ar-side-grid-avatar small">
                                                {avatarUrl ? (
                                                    <img src={avatarUrl} alt="" onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                                                ) : null}
                                                <span style={avatarUrl ? { display: 'none' } : {}}>
                                                    {(p.user?.name || '?')[0].toUpperCase()}
                                                </span>
                                                {emojiReaction && <span className="ar-side-emoji-pop small" key={Date.now()}>{emojiReaction}</span>}
                                            </div>
                                            <span className="ar-side-grid-name">{p.user?.name || 'User'}</span>
                                            {p.hand_raised_at && <span className="ar-side-hand">✋</span>}
                                            {canManage && p.hand_raised_at && (
                                                <button
                                                    className="ar-side-promote-btn"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        try { await API.post(`/v1/audio-rooms/${activeRoom.id}/promote/${p.user_id}`); refreshRoomDetails(); } catch (err) { console.error(err); }
                                                    }}
                                                    title="Promote to speaker"
                                                >
                                                    <i className="fa fa-microphone" />
                                                </button>
                                            )}
                                            {/* Context menu */}
                                            {managingParticipant?.id === p.id && (
                                                <div className="ar-side-ctx-menu" onClick={(e) => e.stopPropagation()}>
                                                    <button onClick={async () => {
                                                        try { await inviteToSpeak(p.user_id); } catch (e) { console.error(e); }
                                                        setManagingParticipant(null);
                                                    }}>
                                                        <i className="fa fa-microphone" /> Invite to speak
                                                    </button>
                                                    <button className="danger" onClick={async () => {
                                                        try { await API.post(`/v1/audio-rooms/${activeRoom.id}/kick/${p.user_id}`); refreshRoomDetails(); } catch (e) { console.error(e); }
                                                        setManagingParticipant(null);
                                                    }}>
                                                        <i className="fa fa-ban" /> Remove
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* ─── EMOJI REACTION BAR ─── */}
                    <div className="ar-side-emoji-bar">
                        {['👍', '🔥', '👏', '❤️', '😂'].map(emoji => (
                            <button
                                key={emoji}
                                className="ar-side-emoji-btn"
                                onClick={() => sendReaction(emoji)}
                                title={`React with ${emoji}`}
                            >
                                {emoji}
                            </button>
                        ))}
                        {/* ✋ Hand: speakers → persistent raise hand; others → ephemeral */}
                        {isSpeaker ? (
                            <button
                                className={`ar-side-emoji-btn ${handRaised ? 'hand-active' : ''}`}
                                onClick={handRaised ? lowerHand : requestToSpeak}
                                title={handRaised ? 'Lower hand' : 'Raise hand'}
                            >
                                ✋
                            </button>
                        ) : (
                            <button
                                className="ar-side-emoji-btn"
                                onClick={() => sendReaction('✋')}
                                title="React with ✋"
                            >
                                ✋
                            </button>
                        )}
                    </div>

                    {/* ─── HOST / CO-HOST EXTRAS ─── */}
                    {canManage && (
                        <div className="ar-side-host-controls">
                            <button
                                className={`ar-side-host-btn ${isMuteLocked ? 'active' : ''}`}
                                onClick={muteAll}
                                title={isMuteLocked ? 'Allow unmute' : 'Mute all'}
                            >
                                <i className={`fa ${isMuteLocked ? 'fa-volume-up' : 'fa-volume-mute'}`} />
                                <span>{isMuteLocked ? 'Unmute' : 'Mute All'}</span>
                            </button>
                            <button
                                className={`ar-side-host-btn ${isRecording ? 'recording' : ''}`}
                                onClick={isRecording ? stopRecording : startRecording}
                                title={isRecording ? 'Stop recording' : 'Record'}
                            >
                                <i className={`fa ${isRecording ? 'fa-stop-circle' : 'fa-circle'}`} />
                                <span>{isRecording ? 'Stop' : 'Record'}</span>
                            </button>
                            <button
                                className={`ar-side-host-btn ${isRoomLocked ? 'active' : ''}`}
                                onClick={toggleLock}
                                title={isRoomLocked ? 'Unlock' : 'Lock'}
                            >
                                <i className={`fa ${isRoomLocked ? 'fa-lock' : 'fa-unlock'}`} />
                                <span>{isRoomLocked ? 'Unlock' : 'Lock'}</span>
                            </button>
                        </div>
                    )}

                    {/* ─── SPEAKER REQUESTS (host/co-host) ─── */}
                    {canManage && handRaisedListeners.length > 0 && (
                        <div className="ar-side-requests">
                            <div className="ar-side-requests-header">
                                <span>✋ {handRaisedListeners.length} request{handRaisedListeners.length > 1 ? 's' : ''}</span>
                                {handRaisedListeners.length > 1 && (
                                    <button
                                        className="ar-side-accept-all"
                                        onClick={async () => {
                                            for (const p of handRaisedListeners) {
                                                try { await API.post(`/v1/audio-rooms/${activeRoom.id}/promote/${p.user_id}`); } catch (e) { console.error(e); }
                                            }
                                            refreshRoomDetails();
                                        }}
                                    >
                                        ✅ Accept All
                                    </button>
                                )}
                            </div>
                            {handRaisedListeners.map(p => (
                                <div key={p.id} className="ar-side-request-item">
                                    <div className="ar-side-request-user">
                                        {getAvatarUrl(p.user) ? (
                                            <img src={getAvatarUrl(p.user)} alt="" className="ar-side-request-avatar" />
                                        ) : (
                                            <div className="ar-side-request-avatar placeholder">
                                                {(p.user?.name || '?')[0].toUpperCase()}
                                            </div>
                                        )}
                                        <span>{p.user?.name || 'Unknown'}</span>
                                    </div>
                                    <div className="ar-side-request-actions">
                                        <button
                                            className="accept"
                                            onClick={async () => {
                                                try { await API.post(`/v1/audio-rooms/${activeRoom.id}/promote/${p.user_id}`); refreshRoomDetails(); } catch (e) { console.error(e); }
                                            }}
                                        >✅</button>
                                        <button
                                            className="reject"
                                            onClick={async () => {
                                                try { await API.post(`/v1/audio-rooms/${activeRoom.id}/reject-speak/${p.user_id}`); refreshRoomDetails(); } catch (e) { console.error(e); }
                                            }}
                                        >❌</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ─── CONTROLS BAR ─── */}
                    <div className="ar-side-controls">
                        {/* Listener: Request Speaker */}
                        {!isSpeaker && (
                            <button
                                className={`ar-side-request-btn ${handRaised ? 'active' : ''}`}
                                onClick={handRaised ? lowerHand : requestToSpeak}
                                title={handRaised ? 'Cancel request' : 'Request speaker'}
                            >
                                <i className="fa fa-microphone" />
                                <span>{handRaised ? 'Cancel' : 'Request'}</span>
                            </button>
                        )}

                        {/* Speaker: Mic toggle */}
                        {isSpeaker && (
                            <div style={{ position: 'relative', display: 'inline-flex' }}>
                                {muteLockMessage && (
                                    <div className="ar-side-mute-tooltip">{muteLockMessage}</div>
                                )}
                                <button
                                    className={`ar-side-mic ${isMuted ? 'muted' : ''}`}
                                    onClick={toggleMute}
                                    title={isMuted ? 'Unmute' : 'Mute'}
                                >
                                    <i className={`fa ${isMuted ? 'fa-microphone-slash' : 'fa-microphone'}`} />
                                </button>
                            </div>
                        )}

                        {/* Speaker (not host): Step Down */}
                        {isSpeaker && !canManage && currentRole !== 'host' && (
                            <button
                                className="ar-side-stepdown"
                                onClick={() => stepDown()}
                                title="Move to listeners"
                            >
                                <i className="fa fa-arrow-down" />
                            </button>
                        )}

                        {/* Host only: End Room */}
                        {currentRole === 'host' && !showEndConfirm && (
                            <button className="ar-side-end" onClick={() => setShowEndConfirm(true)}>
                                End
                            </button>
                        )}
                        {currentRole === 'host' && showEndConfirm && (
                            <div className="ar-side-end-confirm">
                                <button className="ar-side-end" onClick={async () => { setShowEndConfirm(false); await handleEndRoom(); }}>
                                    Yes
                                </button>
                                <button className="ar-side-cancel" onClick={() => setShowEndConfirm(false)}>
                                    No
                                </button>
                            </div>
                        )}

                        {/* All: Leave */}
                        <button className="ar-side-leave" onClick={handleLeaveRoom}>
                            ✌️ Leave
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AudioRoomMiniPlayer;
